import * as CACHE from "@cachables";
import { brktToUsd, usdToBrkt, } from "@store/commons";
import type { LoadedVault, } from "@types";
import { wagmiConfig as config, } from "@wagmiconfig";
import { getAccount, } from "wagmi/actions";
import { getBaseAssetPrice, } from "./getBaseAssetPrice";




/**
 * Calculates the total value locked (TVL) for the current user across all provided vaults and their BRKT balance.
 * This includes the value of deposits in vaults (converted to USD based on the vault's denomination)
 * and the user's BRKT balance (converted to USD).
 * 
 * @remarks returning the value in brkt as well althought we probably will not need it in future screens.
 * 
 * @param vaults - An array of Vault objects representing the vaults to consider for the TVL calculation.
 * @returns An object containing the total TVL in both USD and BRKT (as a string).
 *          - `usd`: Total value locked in USD.
 *          - `brkt`: Total value locked in BRKT, represented as a string to handle large numbers.
 * @throws If the user's address cannot be obtained.
 * @throws If fetching base asset price, BRKT balance, or BRKT rate fails.
 */
export const getYourPlatformTVL = async ( vaults: LoadedVault[] ) => {

	try {

		console.log("getYourPlatformTVL V1 called");

		const account = getAccount(config);
		if(!account.address) {
			throw new Error("User address not found. Cannot calculate TVL.");
		}
		const { address, } = account;

		const total_deposits_usd = (await Promise.all(

			vaults.map(async vault => {

				const { decimals, symbol, } = vault.denomination;
				const baseAssetPrice = await getBaseAssetPrice({ symbol, chainId: 1, }); // price according to chain 1 only...

				const [
					// pending shares are included in current TVL calculation alongside active shares
					pending,
					shares,
					aps,
				] = (await Promise.all([
					CACHE.pendingDepositShares({ wallet: address, vault, }),
					CACHE.sharesOf({ wallet: address, vault, }),
					CACHE.assetsPerShare({ vault, }),
				])).map(bi => Number(bi) / (10**decimals));

				// Calculate the value of the user's shares in this vault in terms of the base asset, then convert to USD
				const vaultDepositValueUsd = (pending + shares) * aps * baseAssetPrice;
				return vaultDepositValueUsd;

			}),

		)).reduce((acc, deposit) => acc + deposit, 0);

		// Get the user's BRKT balance and convert it to USD
		const total_brkt = await CACHE.balanceOfWallet({ token: { symbol: "BRKTETH", chainId: 1, }, }); // Ensure wallet address is passed
		const total_brkt_usd = await brktToUsd(total_brkt);

		// Sum the USD value of vault deposits and the BRKT balance
		const usd = total_deposits_usd + total_brkt_usd;

		// Convert the total USD value back to BRKT
		return {
			brkt: (await usdToBrkt(usd)).toString(),
			usd,
		}

	}
	catch(err) {
		console.warn("failed gaining your platform tvl V1", err);
		return {
			usd: 0,
			brkt: "0",
		}
	}

};




export const getYourPlatformTVL_v2 = async ( vaults: LoadedVault[] ) => {

	try {
 
		const account = getAccount(config);
		if(!account.address) {
			throw new Error("User address not found. Cannot calculate TVL.");
		}
		const total_usd = vaults.reduce((acc, v) => acc + v.exposure.current_value.usd, 0);
		const total_brkt = await CACHE.balanceOfWallet({ token: { symbol: "BRKTETH", chainId: 1, }, });
		const total_brkt_usd = await brktToUsd(total_brkt);
		return {
			usd: total_usd + total_brkt_usd,
			brkt: "-1"
		}
	}
	catch(err) {

		console.warn("failed gaining your platform tvl", err);

		return {
			usd: 0,
			brkt: "0",
		}

	}
}
